import { LoginSocial } from 'components/Blocks/LoginForm/LoginSocial';
import Button from 'components/Forms/Button/Button';
import Form from 'components/Forms/Form/Form';
import FormLine from 'components/Forms/Lib/FormLine/FormLine';
import FormLineError from 'components/Forms/Lib/FormLineError/FormLineError';
import TextInput from 'components/Forms/TextInput/TextInput';
import SimpleLayout from 'components/Layout/SimpleLayout/SimpleLayout';
import { useLoginForm, useLoginFormMeta } from 'components/Pages/Login/formMeta';
import { ButtonWrapperStyled, LoginLostPassLinkStyled } from 'components/Pages/Login/Login.style';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { handleFormErrors } from 'helpers/forms/handleFormErrors';
import { useAuth } from 'hooks/auth/useAuth';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';
import { usePersistStore } from 'store/usePersistStore';
import { BreadcrumbItemType } from 'types/breadcrumb';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

type LoginProps = {
    breadcrumbs: BreadcrumbItemType[];
    shouldOverwriteCustomerUserCart?: boolean;
};

const TEST_IDENTIFIER = 'pages-login';

const Login: FC<LoginProps> = ({ breadcrumbs, shouldOverwriteCustomerUserCart }) => {
    const { t } = useTranslation();
    const { cartUuid } = usePersistStore((s) => s);
    const { url } = useDomainConfig();
    const { query } = useRouter();
    const { login } = useAuth();
    const [resetPasswordUrl] = getInternationalizedStaticUrls(['/reset-password'], url);
    const [formProviderMethods] = useLoginForm();
    const {
        fields: { email: emailField, password: passwordField },
        formName,
    } = useLoginFormMeta(formProviderMethods);

    const onLoginHandler: SubmitHandler<{ email: string; password: string }> = async (data) => {
        let redirectUrl = url;

        if (typeof query.r === 'string') {
            redirectUrl = query.r;
        }

        const loginResult = await login(
            {
                email: data.email,
                password: data.password,
                previousCartUuid: cartUuid,
                deleteOldCustomerCart: false,
                shouldOverwriteCustomerUserCart,
            },
            redirectUrl,
        );

        handleFormErrors(loginResult.error, formProviderMethods, t);
    };

    return (
        <SimpleLayout breadcrumb={breadcrumbs} heading={t('Login')}>
            <FormProvider {...formProviderMethods}>
                <Form data-testid={TEST_IDENTIFIER} onSubmit={formProviderMethods.handleSubmit(onLoginHandler)}>
                    <Controller
                        name={emailField.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <FormLine bottomGap>
                                <TextInput
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id={`${formName}-${emailField.name}`}
                                    isTouched={isTouched}
                                    label={emailField.label}
                                    name={emailField.name}
                                    type="text"
                                />
                                <FormLineError
                                    data-testid={`${formName}-${emailField.name}-error`}
                                    error={error}
                                    inputType="text-input"
                                    textInputSize="small"
                                />
                            </FormLine>
                        )}
                    />
                    <Controller
                        name={passwordField.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <FormLine>
                                <TextInput
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id={`${formName}-${passwordField.name}`}
                                    isTouched={isTouched}
                                    label={passwordField.label}
                                    name={passwordField.name}
                                    type="password"
                                />
                                <FormLineError
                                    data-testid={`${formName}-${passwordField.name}-error`}
                                    error={error}
                                    inputType="text-input-password"
                                    textInputSize="small"
                                />
                            </FormLine>
                        )}
                    />
                    <LoginLostPassLinkStyled href={resetPasswordUrl}>
                        {t('Lost your password?')}
                    </LoginLostPassLinkStyled>

                    <ButtonWrapperStyled>
                        <Button data-testid={`${TEST_IDENTIFIER}-submit`} type="submit">
                            {t('Log in')}
                        </Button>
                    </ButtonWrapperStyled>

                    <LoginSocial itemsInLine shouldOverwriteCustomerUserCart cartUuid={cartUuid} />
                </Form>
            </FormProvider>
        </SimpleLayout>
    );
};

export default Login;
